import React from "react";
import { TextField, SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import { UseFormRegisterReturn } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";
import { textFieldSx as baseTextFieldSx } from "./formStyles";

export interface FormTextFieldProps {
  // react-hook-form mode (pass register result)
  name?: string;
  register?: UseFormRegisterReturn;
  error?: boolean;
  helperText?: string;
  // standalone mode
  value?: string;
  onChange?: (value: string) => void;
  // common
  placeholder?: string;
  type?: "text" | "number" | "date";
  fullWidth?: boolean;
  maxLength?: number;
  disabled?: boolean;
  inputProps?: Record<string, unknown>;
  size?: "small" | "medium";
  hintText?: string;
  sx?: SxProps<Theme>;
}

/**
 * Styled MUI TextField with dt tokens.
 *
 * Supports both react-hook-form mode (via `register`) and standalone mode
 * (via `value` + `onChange`). Based on WO textFieldSx + SR/RFS focus styling.
 */
const FormTextField: React.FC<FormTextFieldProps> = ({
  name,
  register,
  error,
  helperText,
  value,
  onChange,
  placeholder,
  type = "text",
  fullWidth = true,
  maxLength,
  disabled,
  inputProps,
  size = "small",
  hintText,
  sx,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // Determine helper text: error text takes priority, then hint text
  const displayHelperText = helperText || (hintText && !error ? hintText : undefined);

  // Build inputProps with maxLength if specified
  const mergedInputProps: Record<string, unknown> = {
    ...(inputProps || {}),
    ...(maxLength ? { maxLength } : {}),
  };

  // For type="date", force shrink on InputLabel
  const inputLabelProps = type === "date" ? { shrink: true } : undefined;

  // Hint text styling
  const helperTextSx =
    !error && hintText && !helperText
      ? {
          fontStyle: "italic",
          color: dt.textMuted,
          fontFamily: dt.font,
        }
      : { fontFamily: dt.font };

  // react-hook-form mode
  if (register) {
    return (
      <TextField
        {...register}
        fullWidth={fullWidth}
        size={size}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        error={error}
        helperText={displayHelperText}
        inputProps={mergedInputProps}
        InputLabelProps={inputLabelProps}
        FormHelperTextProps={{ sx: helperTextSx }}
        sx={{
          ...(baseTextFieldSx as Record<string, unknown>),
          ...((sx as Record<string, unknown>) || {}),
        }}
      />
    );
  }

  // standalone mode
  return (
    <TextField
      name={name}
      fullWidth={fullWidth}
      size={size}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      error={error}
      helperText={displayHelperText}
      inputProps={mergedInputProps}
      InputLabelProps={inputLabelProps}
      FormHelperTextProps={{ sx: helperTextSx }}
      sx={{
        ...(baseTextFieldSx as Record<string, unknown>),
        ...((sx as Record<string, unknown>) || {}),
      }}
    />
  );
};

export default FormTextField;
